import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { getSessionStorage } from '@libs/storage';

import { SiteLayoutWidget } from '@features/shared/ui/SiteLayout/SiteLayoutWidget';
import { useRefereeRooms } from '@features/video/hooks/useRefereeRooms';
import { formatRoomsSearch, getRefereeRoomPath } from '@features/video/lib/ticket';
import { RoomRef } from '@features/video/types';
import { MicIssueBanner } from '@features/video/ui/MicIssueBanner';
import { PrivateRoomModal } from '@features/video/ui/PrivateRoomModal';
import { RefereeControls } from '@features/video/ui/RefereeControls';
import { RoomPickerModalWidget } from '@features/video/ui/RoomPickerModal';

import { RefereeRoomPage } from './RefereeRoomPage';

type Props = {
  clubId: string;
  // комнаты мультикомнаты: первая — из пути страницы, остальные — из search `rooms`
  rooms: RoomRef[];
  // из ссылки `.../private/<userId>`: модалка открывается сама после входа
  privateUserId?: string;
};

// Судья был в комнате в этой вкладке и с каким микрофоном: после перезагрузки входим сами.
// Ключ на вкладку простой; забывает его только выход. Состав комнат — в адресе
const RESUME_KEY = 'video:referee';

// Страница судьи: комнаты, контролы в липкой шапке сайта, модалка приватной комнаты и
// модалка выбора турниров и комнат
export function RefereeRoomPageWidget({ clubId, rooms, privateUserId }: Props) {
  const room = useRefereeRooms({ clubId, rooms });
  const navigate = useNavigate();
  const isConnected = room.connection === 'live' || room.connection === 'reconnecting';

  // Вход и состояние микрофона помним на вкладку, выход забывает; потеря связи — нет, после
  // перезагрузки входим снова. Без клика браузер может не дать звук — плашка попросит нажатие
  const { join, leave, toggleMic, micOn, devices } = room;
  const inRoom = room.connection !== 'setup';
  useEffect(() => {
    if (inRoom) getSessionStorage().setItem(RESUME_KEY, JSON.stringify({ micOn }));
  }, [inRoom, micOn]);
  const onLeave = useCallback(() => {
    getSessionStorage().removeItem(RESUME_KEY);
    leave();
  }, [leave]);
  // Входим, когда микрофон уже получен; сначала намерение по микрофону, вход — следующим
  // рендером, когда `join` его увидит
  const refResumed = useRef(false);
  useEffect(() => {
    if (refResumed.current || room.connection !== 'setup' || devices.status !== 'ready') return;
    const saved = readResume();
    if (saved && saved.micOn !== micOn) {
      toggleMic();
      return;
    }
    refResumed.current = true;
    if (saved) join();
  }, [room.connection, devices.status, micOn, toggleMic, join]);

  // Адрес мультикомнаты: первая комната — путь, остальные — search; приват — сегмент пути
  const goTo = useCallback(
    (next: RoomRef[], userId?: string) => {
      const [first, ...rest] = next;
      if (!first) return;
      const params = {
        clubId,
        tournamentId: first.tournamentId,
        roomNumber: String(first.roomNumber),
      };
      const search = { rooms: formatRoomsSearch(rest) };
      if (userId) {
        navigate({
          to: '/club/$clubId/tournament/$tournamentId/referee/$roomNumber/private/$userId',
          params: { ...params, userId },
          search,
          replace: true,
        });
      } else {
        navigate({
          to: '/club/$clubId/tournament/$tournamentId/referee/$roomNumber',
          params,
          search,
          replace: true,
        });
      }
    },
    [clubId, navigate],
  );

  // Приват по ссылке: один раз на каждый userId из адреса, когда комната уже в эфире
  const { openPrivate, closePrivate } = room;
  const refOpened = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (room.connection !== 'live' || !privateUserId || refOpened.current === privateUserId) return;
    refOpened.current = privateUserId;
    openPrivate(privateUserId);
  }, [room.connection, privateUserId, openPrivate]);

  // Закрытие модалки убирает userId из адреса: ссылка снова на комнаты
  const onClosePrivate = useCallback(() => {
    closePrivate();
    refOpened.current = undefined;
    if (privateUserId) goTo(rooms);
  }, [closePrivate, privateUserId, goTo, rooms]);

  const privateRoomHref = useCallback(
    (userId: string) => getRefereeRoomPath(clubId, rooms, userId),
    [clubId, rooms],
  );

  const onOpenGame = useCallback(
    (gameId: string, tournamentId: string) => {
      window.open(`/club/${clubId}/tournament/${tournamentId}/game/${gameId}`, '_blank');
    },
    [clubId],
  );

  // Выбор турниров и комнат: новый состав — новый адрес, комнаты подключатся сами
  const [isPickerOpen, setPickerOpen] = useState(false);
  const openPicker = useCallback(() => setPickerOpen(true), []);
  const closePicker = useCallback(() => setPickerOpen(false), []);
  const applyRooms = useCallback(
    (next: RoomRef[]) => {
      setPickerOpen(false);
      if (next.length) goTo(next, privateUserId);
    },
    [goTo, privateUserId],
  );

  const privateRoom = room.privateRoom;
  // основная комната — из пути страницы, в модалке выбора ее не убрать
  const [mainRoom] = rooms;

  // Флаг сняли при открытой модалке — другой судья вернул игрока или ссылка устарела:
  // модалке делать нечего, закрываем штатно, фокус и метка возвращаются. Игрока нет
  // в списке комнаты — флаг неизвестен, не трогаем
  const privatePlayerKey = privateRoom?.player.key;
  const isStillPrivate = room.players.find((p) => p.key === privatePlayerKey)?.inPrivateRoom;
  useEffect(() => {
    if (privatePlayerKey && isStillPrivate === false) onClosePrivate();
  }, [privatePlayerKey, isStillPrivate, onClosePrivate]);

  return (
    <SiteLayoutWidget
      clubId={clubId}
      hideCreateTournament
      actions={
        isConnected && (
          <RefereeControls
            micOn={room.micOn}
            disabled={room.connection !== 'live'}
            devices={room.devices}
            meterTrack={room.meterTrack}
            onToggleMic={room.toggleMic}
            onLeave={onLeave}
          />
        )
      }
    >
      <RefereeRoomPage
        rooms={room.rooms}
        onPickRooms={openPicker}
        isPickerOpen={isPickerOpen}
        onRetryRoom={room.retryRoom}
        connection={room.connection}
        error={room.error}
        referees={room.referees}
        players={room.players}
        speakingProxy={room.speakingProxy}
        micOn={room.micOn}
        onToggleMic={room.toggleMic}
        devices={room.devices}
        onJoin={room.join}
        onRetry={room.join}
        onMute={room.mute}
        onKick={room.kick}
        onRestore={room.restore}
        onInvitePrivate={room.invitePrivate}
        onReturnToRoom={room.returnToRoom}
        onPinnedChange={room.setPinned}
        onShownChange={room.setShown}
        privateRoomHref={privateRoomHref}
        onOpenGame={onOpenGame}
      />
      <MicIssueBanner issue={room.micIssue} audioBlocked={room.audioBlocked} onFix={room.fixMic} />
      {isPickerOpen && mainRoom && (
        <RoomPickerModalWidget
          clubId={clubId}
          main={mainRoom}
          selected={rooms}
          onApply={applyRooms}
          onClose={closePicker}
        />
      )}
      {privateRoom && (
        <PrivateRoomModal
          player={privateRoom.player}
          connection={privateRoom.connection}
          error={privateRoom.error}
          referees={privateRoom.referees}
          speakingProxy={privateRoom.speakingProxy}
          micOn={room.micOn}
          devices={room.devices}
          meterTrack={privateRoom.meterTrack}
          onToggleMic={room.toggleMic}
          onMute={(deviceId) => room.mute(privateRoom.player.userId, deviceId)}
          // игрок вернулся в общую комнату — судье в привате делать нечего
          onReturnPlayer={() => {
            const { userId, tournamentId, roomNumber } = privateRoom.player;
            room.returnToRoom(
              userId,
              tournamentId !== undefined && roomNumber !== undefined
                ? { tournamentId, roomNumber }
                : null,
            );
            onClosePrivate();
          }}
          onRetry={() => {
            closePrivate();
            openPrivate(privateRoom.player.userId);
          }}
          onClose={onClosePrivate}
        />
      )}
    </SiteLayoutWidget>
  );
}

/*
    ____,-------------------------------,____
    \   |            Helpers            |   /
    /___|-------------------------------|___\
*/

function readResume(): { micOn: boolean } | null {
  try {
    const raw = getSessionStorage().getItem(RESUME_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    const micOn =
      typeof parsed === 'object' && parsed !== null && 'micOn' in parsed ? parsed.micOn : false;
    return { micOn: micOn === true };
  } catch {
    return null;
  }
}
